import React, { useCallback, useReducer, useEffect } from "react";
import { useForm } from "react-hook-form";
import { getCrops, deleteCrop } from "../../services/crops/cropsService";

import ButtonNew from "../../shared/components/buttonNew/ButtonNew";
import InputSearch from "../../shared/components/inputSearch/InputSearch";
import ButtonFilter from "../../shared/components/buttonFilter/ButtonFilter";
import ButtonDownload from "../../shared/components/buttonDownload/ButtonDownload";
import ButtonEdit from "../../shared/components/buttonEdit/ButtonEdit";
// import ButtonDelete from "../../shared/components/buttonDelete/ButtonDelete";
import CustomPaginator from "../../shared/components/paginator/CustomPaginator";
import DeleteModal from "../../shared/components/deleteModal/DeleteModal";
import TableHeader from "../../shared/components/tableHeader/TableHeader";

const ITEMS_PER_PAGE = 10;
const TABLE_HEADERS = ['Cultivo', 'Suelo', 'Riego', 'Empresa', 'Acciones'];


// Reducer
const initialState = {
  crops: [],
  currentPage: 1,
  totalPages: 3,
  isLoading: false,
  error: null,
  deleteModalOpen: false,
  selectedCropId: null,
  filters: { emailFilter: '', statusFilter: '' }
};

function reducer(state, action) {
  switch (action.type) {
      case 'SET_CROPS':
          return { ...state, crops: action.payload };
      case 'SET_LOADING':
          return { ...state, isLoading: action.payload };
      case 'SET_ERROR':
          return { ...state, error: action.payload };
      case 'SET_PAGE':
          return { ...state, currentPage: action.payload };
      case 'SET_TOTAL_PAGES':
          return { ...state, totalPages: action.payload };
      case 'SET_FILTERS':
          return { ...state, filters: action.payload };
      case 'TOGGLE_DELETE_MODAL':
          return { 
              ...state, 
              deleteModalOpen: !state.deleteModalOpen,
              selectedCropId: action.payload 
          };
      default:
          return state;
  }
}

// Components

const CropTable = React.memo(({ crops, onDelete, error }) => {
  if (error) {
      return (
          <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
              <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                  Error obteniendo los datos
              </td>
          </tr>
      );
  }
  if (crops.length === 0) {
      return (
          <tr className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
              <td colSpan={TABLE_HEADERS.length} className="text-center px-6 py-3 whitespace-nowrap">
                  No se encontraron registros
              </td>
          </tr>
      );
  }

  return crops.map((crop) => (
    <tr key={crop.id} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100">
      <th scope="row" className="px-6 py-3 font-medium whitespace-nowrap">
        <span className="font-bold">Id: {crop.id}</span> <br />
        <span className="font-bold">{crop.name || 'Cultivo sin especificar'}</span> <br />
        <span className="font-normal">Tipo: {crop.cropType?.name || 'Sin especificar'}</span> <br />
        <span className="font-normal">Ubicación: {crop?.location}</span> <br />
        <span className="font-normal">Variedad: {crop?.variety}</span> <br />
        <span className="font-normal">Edad: {crop?.cropAge}</span> <br />
        <span className="font-normal">Área: {crop?.area}</span> <br />
        <span className="font-normal">¿Siembras anteriores?: {(crop && crop.previousPlantings) ? 'sí' : 'no'}</span>
      </th>
      
      <td className="px-6 py-3">
        <span>Tipo: {crop.soilType || 'Suelo sin especificar'}</span> <br/>
        <span>¿Análisis del suelo?: {(crop && crop.soilAnalysis) ? 'sí' : 'no'}</span> <br />
        <span>Productividad: {crop?.productivityPerPlant}</span> <br />
        <span>Personas por área: {crop?.peopleInArea || 'Sin especificar'}</span>
      </td>

      <td className="px-6 py-3">
        <span>Sistema: {crop.irrigation || 'Riego sin especificar'}</span> <br />
        <span>Tipo: {crop?.engine}</span> <br />
        <span>Frecuencia: {crop?.frequency}</span> <br />
        <span>Volumen: {crop?.volume}</span> <br />
        <span>¿Asesoría técnica?: {(crop && crop.advisoryService) ? 'sí' : 'no'}</span>
      </td>

      <td className="px-6 py-3">
        <span>{crop.company || 'Companía sin especificar'}</span> <br />
        <span>Contacto: {crop?.contact}</span> <br />
        <span>Email: {crop?.email}</span> <br />
        <span>Teléfono: {crop?.phone}</span>
      </td>

      <td className="px-6 py-3">
        <ButtonEdit to={`/crops/${crop.id}`}></ButtonEdit>
        {/* <ButtonDelete onClick={() => onDelete(crop.id)} /> */}
      </td>
    </tr>
  ));
});

const FilterForm = React.memo(({ onSubmit, register }) => (
  <form onSubmit={onSubmit} className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 mb-2">
      <div className="flex items-center w-full md:w-7/12">
          <InputSearch
              placeholder="Cultivo" 
              name="cropFilter" 
              id="cropFilter"
              {...register('cropFilter')}
          />
      </div>
      
      <div className="flex flex-col sm:flex-row items-center justify-end w-full md:w-1/3 space-y-3 sm:space-y-0 sm:space-x-4">
          <ButtonFilter type="submit" />
          <ButtonDownload />
      </div>
  </form>
));

const CropsView = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { register, handleSubmit } = useForm();

  const loadCrops = useCallback(async (filters = {}) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      dispatch({ type: 'SET_ERROR', payload: null });
      const data = await getCrops(state.currentPage, ITEMS_PER_PAGE, filters);
      dispatch({ type: 'SET_CROPS', payload: data.content });
      dispatch({ type: 'SET_TOTAL_PAGES', payload: data.totalPages });
      
      if (data.totalPages < state.currentPage) {
          dispatch({ 
              type: 'SET_PAGE', 
              payload: data.totalPages === 0 ? 1 : data.totalPages 
          });
      }
    } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error.message });
        console.error('Error fetching crops:', error);
    } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.currentPage]);

  useEffect(() => {
      const newFilters = {};
      if (state.filters.emailFilter) newFilters.email = state.filters.emailFilter;
      if (state.filters.statusFilter) newFilters.active = state.filters.statusFilter;
      loadCrops(newFilters);
  }, [state.filters, loadCrops]);

  const handleFilterSubmit = handleSubmit((data) => {
      dispatch({ type: 'SET_FILTERS', payload: data });
  });

  const handlePageChange = useCallback((page) => {
      if (page === state.currentPage || page < 1 || page > state.totalPages) return;
      dispatch({ type: 'SET_PAGE', payload: page });
  }, [state.currentPage, state.totalPages]);

  const toggleDeleteModal = useCallback((cropId) => {
      dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: cropId });
  }, []);

  const handleDelete = async () => {
    try {
        const response = await deleteCrop(state.selectedCropId);
        if (response) {
            dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: null });
            await loadCrops();
        }
    } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Error al eliminar el cultivo' });
        console.error('Error al eliminar el cultivo: ', error);
    }
  };
  return (
    <div className="flex flex-col p-3">
      <div className="flex max-sm:flex-col items-center justify-between mb-4">
        <h1 className="text-3xl font-semibold text-blue-600">Cultivos</h1>
        <ButtonNew to={'/crops/newCrop'}></ButtonNew>
      </div>

      <FilterForm onSubmit={handleFilterSubmit} register={register} />

      <hr className="my-2" />

      {state.isLoading ? (
          <div className="text-center py-4">Cargando...</div>
      ) : (
          <div className="relative overflow-x-auto shadow-lg sm:rounded-lg">
              <table className="w-full text-sm text-left border border-gray-200">
                  <TableHeader headerOptions={TABLE_HEADERS}/>
                  <tbody>
                      <CropTable 
                          crops={state.crops} 
                          onDelete={toggleDeleteModal}
                          error={state.error}
                      />
                  </tbody>
              </table>
          </div>
      )}

      {!state.error && (
          <CustomPaginator 
              currentPage={state.currentPage} 
              totalPages={state.totalPages} 
              handlePageChange={handlePageChange}
          />
      )}

      <DeleteModal
          isOpen={state.deleteModalOpen}
          toggleDeleteModal={() => toggleDeleteModal(null)}
          deleteModal={handleDelete}
          nameParameter="cultivo"
      />
    </div>
  );
}

export default CropsView;